import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import {
    Toolbar,
    Divider,
    ListItem,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Drawer,
    Collapse,
    Box,
    Typography,
} from '@mui/material'
import { Grid } from '../UniversalComponents'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { useNavMenu } from '../../context/NavMenuContext'
import darklogo from '../../assets/osmosis/Logos/darklogo.png'
import { navPermissionsCheck } from '../../utilities/functionTools'
import { useAuth } from '../../context/AuthenticationContext'
import { useGlobalState } from '../../context/GlobalStateContext'
const packageInfo = require('../../../package.json')

const NavMenuDrawer = ({
    handleDrawerToggle,
    mobileOpen,
    container,
    drawerWidth,
}) => {
    const { navMenuLinks } = useNavMenu()
    const [state, setNewState] = useState({})
    const setState = (newState) => setNewState({ ...state, ...newState })
    const { globalState } = useGlobalState()
    const { oupUser } = globalState

    const { userProfile } = oupUser
    const { superAdmin, userProfilePermissions } = userProfile
    const isSuperAdmin = superAdmin !== true

    // Brand-styled drawer surface: OSMO-black paper, hairline border, rounded
    // hover targets, and a green accent on the active route.
    const drawerPaperSx = {
        boxSizing: 'border-box',
        width: drawerWidth,
        backgroundColor: 'background.default',
        borderRight: '1px solid',
        borderColor: 'divider',
        '& .MuiListItemButton-root': {
            borderRadius: 1.5,
            mx: 1,
            my: 0.25,
            '&:hover': { backgroundColor: 'action.hover' },
        },
        '& a.active': {
            color: 'primary.main',
            '& .MuiListItemIcon-root': { color: 'primary.main' },
            '& .MuiListItemButton-root': {
                backgroundColor: 'action.hover',
                boxShadow: 'inset 3px 0 0 0 var(--osmo-green)',
            },
        },
    }

    // This is where you style the menu items
    const menuDrawer = (
        <div>
            <Toolbar>
                <Grid container>
                    <NavLink to="/">
                        <Box
                            component="img"
                            sx={{
                                height: 35,
                                marginLeft: -1,
                                position: 'relative',
                                top: 5,
                            }}
                            alt="Osmosis"
                            src={darklogo}
                        />
                    </NavLink>
                    <Grid item xs={12} sx={{ textAlign: 'right' }}>
                        <Typography
                            variant="overline"
                            sx={{ color: 'text.secondary', fontSize: 11 }}
                        >
                            v{packageInfo.version}
                        </Typography>
                    </Grid>
                </Grid>
            </Toolbar>
            <Divider />
            <List key="main-list" sx={{ pt: 1 }}>
                {
                    // Add icon in the map here and on the child .map
                }
                {navMenuLinks.map(
                    ({ path, name, children, icon, superAdmin: superAdminOnly }) => {
                        if (!children) {
                            return (
                                <ListItem
                                    key={`${name}.li`}
                                    disablePadding
                                    component={NavLink}
                                    to={path}
                                    sx={{ color: 'inherit' }}
                                >
                                    <ListItemButton key={`${name}.li.button`}>
                                        <ListItemIcon
                                            key={`${name}.li.button.icon`}
                                            children={icon}
                                        />

                                        <ListItemText
                                            sx={{ color: 'inherit' }}
                                            primary={name}
                                            key={`${name}.li.button.text`}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            )
                        } else {
                            // A super admin sees every child. The router in
                            // NavMenuContext already grants them each of these
                            // routes, so gating the drawer more tightly than the
                            // router only hid pages they could already open —
                            // which is what kept a newly added child (with no
                            // permission key on any profile yet) invisible to
                            // everyone. `superAdminOnly` is the nav group's own
                            // flag and still ungates groups like System whose
                            // children carry no permission key of their own.
                            const canSeeChild = (child: any) =>
                                navPermissionsCheck(
                                    userProfilePermissions,
                                    child.name,
                                    superAdmin === true || superAdminOnly
                                ) === false

                            const hasPermission = children?.some(canSeeChild)
                            const superAdminProtected =
                                isSuperAdmin && superAdminOnly === true

                            if (hasPermission && !superAdminProtected) {
                                return (
                                    <div key={`${name}.div`}>
                                        <ListItemButton
                                            onClick={() =>
                                                setState({
                                                    [name]: !state[name],
                                                })
                                            }
                                            key={`${name}.li.button`}
                                        >
                                            <ListItemIcon
                                                key={`${name}.li.button.icon`}
                                                children={icon}
                                            />

                                            <ListItemText
                                                primary={name}
                                                key={`${name}.li.text`}
                                            />
                                            {state[name] ? (
                                                <ExpandLess />
                                            ) : (
                                                <ExpandMore />
                                            )}
                                        </ListItemButton>

                                        <Collapse
                                            in={state[name]}
                                            timeout="auto"
                                            unmountOnExit
                                            key={`${name}.li.collapse`}
                                        >
                                            <List
                                                component="div"
                                                disablePadding
                                                key={`${name}.li.collapse.list`}
                                            >
                                                {children?.map((child) => {
                                                    if (canSeeChild(child)) {
                                                        return (
                                                            <ListItemButton
                                                                sx={{
                                                                    pl: 4,
                                                                    color: 'inherit',
                                                                }}
                                                                key={`${child.name}.li.button`}
                                                                component={
                                                                    NavLink
                                                                }
                                                                to={child.path}
                                                            >
                                                                <ListItemText
                                                                    key={`${child.name}.li.text`}
                                                                    primary={
                                                                        child.name
                                                                    }
                                                                />
                                                            </ListItemButton>
                                                        )
                                                    } else {
                                                        return null
                                                    }
                                                })}
                                            </List>
                                        </Collapse>
                                    </div>
                                )
                            } else {
                                return null
                            }
                        }
                    }
                )}
            </List>
        </div>
    )
    return (
        <>
            <Drawer
                container={container}
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true, // Better open performance on mobile.
                }}
                sx={{
                    display: { xs: 'block', sm: 'none' },
                    '& .MuiDrawer-paper': drawerPaperSx,
                }}
            >
                {menuDrawer}
            </Drawer>
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', sm: 'block' },
                    '& .MuiDrawer-paper': drawerPaperSx,
                }}
                open
            >
                {menuDrawer}
            </Drawer>
        </>
    )
}

export default NavMenuDrawer
